import React, { Suspense } from 'react'
import classes from './RouteSettingsModal.module.scss'
import Modal from '@/components/ui/Modal'
import Typography from '@/components/ui/Typography'
import Button from '@/components/ui/Buttons/Button'
import { ReactComponent as SettingsIcon } from '@/assets/icons/cogs.svg'
import { useForm } from './RouteSettingsModal.hooks'
import { Form, Formik } from 'formik'
import FormLabel from '@/components/ui/FormLabel'
import { Links, RouteType, RouteTypeOptions, RuntimeOptions } from '@/config/app'
import Autocomplete from '@/components/ui/Autocomplete'
import { TimezoneOptions } from '@/config/timezones'
import { Puff } from '@/components/ui/Loaders'
import Switch from '@/components/ui/Switch'
import Link from '@/components/ui/Link'
import { EstimatedCost } from '@/components/EstimatedCost'
import CronInput from '@/components/CronInput'
import ScheduleSection from '@/components/ScheduleSection'
import Input from '@/components/ui/Input'
import { useGetRoute } from '@/lib/react-query'
import { formatSecureValue } from '@/utils/string'
import CopyAdornment from '@/components/ui/CopyAdornment'

export type RouteSettingsModalProps = { onClose: () => void; projectId: number; routeId: number }

const RouteSettingsModal = ({ onClose, projectId, routeId }: RouteSettingsModalProps) => {
  const formikProps = useForm(projectId, routeId, onClose)
  const {
    data: { route },
  } = useGetRoute(projectId, routeId)

  return (
    <Formik {...formikProps}>
      {({ values, handleSubmit, isSubmitting, handleChange, handleBlur, setFieldValue, errors }) => (
        <Form>
          <Modal onClose={onClose} isOpen>
            <Modal.Header>
              <div>
                <Modal.Icon>
                  <SettingsIcon />
                </Modal.Icon>
                <Typography variant={'xl'}>Route settings</Typography>
              </div>
              <div>
                <Modal.Close onClick={onClose} />
              </div>
            </Modal.Header>
            <Modal.Content>
              <div className={classes.root}>
                <Typography variant={'xl'}>General</Typography>

                <div>
                  <FormLabel>Language</FormLabel>
                  <Autocomplete
                    name={'runtime'}
                    options={RuntimeOptions}
                    value={values.runtime}
                    onChange={setFieldValue}
                    disableClearable
                    selectOnly
                  />
                </div>
                <div>
                  <FormLabel>Route type</FormLabel>
                  <Autocomplete
                    name={'type'}
                    options={RouteTypeOptions}
                    value={values.type}
                    onChange={setFieldValue}
                    disableClearable
                    selectOnly
                  />
                </div>
                {values.type === RouteType.Cron && (
                  <>
                    <div>
                      <Typography variant={'md'}>Schedule</Typography>
                      <ScheduleSection value={values.schedule} />
                      <br />
                      <CronInput name={'schedule'} />
                    </div>

                    <div>
                      <Typography variant={'md'}>Estimated cost</Typography>
                      <Suspense fallback={<Typography variant={'caption'}>Calculating...</Typography>}>
                        <EstimatedCost value={values.schedule} />
                      </Suspense>
                    </div>
                    <div>
                      <FormLabel>Schedule timezone</FormLabel>
                      <Autocomplete
                        name={'scheduleTimeZone'}
                        options={TimezoneOptions}
                        value={values.scheduleTimeZone}
                        onChange={setFieldValue}
                        disableClearable
                      />
                    </div>
                  </>
                )}
                <br />

                <Typography variant={'xl'}>Security</Typography>
                <div className={classes.switch}>
                  <Typography isBold>{'Auth enabled'}</Typography>
                  <Switch
                    checked={values.authEnabled}
                    onChange={(event) => setFieldValue('authEnabled', event.target.checked)}
                  />
                </div>
                <div>
                  {values.authEnabled && (
                    <Input
                      disabled
                      value={formatSecureValue(route.apiKey)}
                      endAdornment={<CopyAdornment value={route.apiKey ?? ''} />}
                    />
                  )}
                </div>

                <br />
                <Typography variant={'xl'}>Logging</Typography>
                <Typography>
                  By enabling this option you accept that we collect and store request info for this route. This will
                  help you analyze errors. You can always activate or deactivate this option later. You can read our
                  privacy notice{' '}
                  <Link href={Links.privacyPolicy} isExternal>
                    here
                  </Link>
                  .
                </Typography>
                <div className={classes.switch}>
                  <Typography isBold>{values.loggingEnabled ? 'Logging enabled' : 'Logging disabled'}</Typography>
                  <Switch
                    checked={values.loggingEnabled}
                    onChange={(event) => setFieldValue('loggingEnabled', event.target.checked)}
                  />
                </div>
              </div>
            </Modal.Content>
            <Modal.Footer>
              <Button
                onClick={() => handleSubmit()}
                disabled={isSubmitting}
                StartIcon={isSubmitting ? <Puff /> : undefined}
              >
                Update route
              </Button>
            </Modal.Footer>
          </Modal>
        </Form>
      )}
    </Formik>
  )
}

export default RouteSettingsModal
